/**
 * SUPPLY-CHAIN DISRUPTION OVERLAY — three lanes on the Command Center calendar.
 *
 * Brian: "highlight every major Chinese holiday, that's JD, and every major
 * Ethiopian holiday, that's SRE... Overlay the major American holidays as
 * well... That way we know when there might be a disruption."
 *
 * ⚠️ 390px FIRST. Three overlays on a phone is a real design problem and the
 * obvious implementation — three coloured washes on a day cell — is unreadable
 * soup. So:
 *
 *   • A day cell gets COMPACT CODE MARKS (`JD` `SRE` `US`), never a wash. Text
 *     survives at any size and does not fight the championship chips that are
 *     the cell's actual content.
 *   • ⚠️ COLOUR IS NEVER THE ONLY CARRIER OF MEANING. Every mark carries its
 *     lane CODE as text and a GLYPH (▲ ■ ●); severity is carried by a text
 *     label and a border weight, not by hue. Read it in greyscale and nothing
 *     is lost.
 *   • The detail — official dates, effective window, provenance, what is
 *     UNKNOWN and why — lives in the DAY PANEL, which is already the pattern
 *     this calendar uses for championships. The grid stays scannable; the panel
 *     carries the reading.
 *
 * 🔴 WHAT THE BANNER CLAIMS IS NARROWER THAN IT LOOKS, deliberately. It reports
 * championships whose EVENT DATE lands in a disruption window. It does NOT
 * claim a delivery-date collision, because SR cannot compute one on this
 * surface yet — see `KNOWN_GAPS.delivery-window-link`. The copy says "event
 * date", and the banner states the gap in-product rather than letting a rep
 * infer a guarantee that is not there.
 */
import { useMemo, useState } from 'react'
import {
  LANES, disruptionsOn, eventDateCollisions, officialLabel, windowLabel,
  windowDays, officialDays, sourceFor,
} from '../../../lib/supplyChainDisruption.js'
import { KNOWN_GAPS } from '../../../data/supplyChainHolidays.js'
import styles from './pursuit.module.css'

/* ---- cell marks --------------------------------------------------------- */

/**
 * The marks drawn inside a day cell. Tiny by design.
 *
 * `reach === 'window'` (the shoulder — inside the effective window but outside
 * the declared holiday) renders HOLLOW, because that distinction is the entire
 * reason this feature exists: the statutory calendar says "open", the factory
 * is not. A rep has to be able to see which kind of day they are looking at.
 */
export function DisruptionCellMarks({ entry }) {
  if (!entry) return null
  const { list = [], makeup } = entry
  const lanes = ['JD', 'SRE', 'US']
    .map((code) => {
      const hits = list.filter((h) => h.lane === code)
      if (!hits.length) return null
      const official = hits.some((h) => h.reach !== 'window')
      const worst = hits[0]
      return { code, official, worst, count: hits.length }
    })
    .filter(Boolean)

  if (!lanes.length && !makeup) return null

  return (
    <div className={styles.disMarks} aria-hidden="false">
      {lanes.map(({ code, official, worst }) => (
        <span
          key={code}
          className={`${styles.disMark} ${official ? styles.disMarkOfficial : styles.disMarkShoulder}`}
          data-lane={code}
          data-sev={worst.severity}
          /* The title is the only place a hover user gets the full story on the
             grid; the day panel is the real answer. */
          title={`${LANES[code].label} — ${worst.name}: ${worst.severityMeta.label}${official ? '' : ' (effective window, not an official holiday)'}`}
        >
          <i className={styles.disGlyph}>{LANES[code].glyph}</i>
          {code}
        </span>
      ))}
      {makeup && (
        /* THE INVERSE TRAP: a weekend JD actually WORKS. Rendered because a
           calendar that only knows about closures shows this day wrong in the
           opposite direction. */
        <span className={styles.disMarkWork} title={`JD — China: mandated make-up WORKING day for ${makeup.forHoliday}. The factory is open on this weekend day.`}>
          <i className={styles.disGlyph}>↻</i>JD OPEN
        </span>
      )}
    </div>
  )
}

/* ---- provenance --------------------------------------------------------- */

/** One source, with its publisher, whether the PAGE was dated, and its link. */
function SourceLine({ keyName }) {
  const s = sourceFor(keyName)
  if (!s) return null
  return (
    <li className={styles.disSource}>
      <span className={styles.disSourcePub}>
        {s.official ? 'OFFICIAL' : 'INDUSTRY'} · {s.publisher}
      </span>
      {s.doc && <span className={styles.disSourceDoc}>{s.doc}</span>}
      <span className={styles.disSourceDate}>
        Page dated: {s.dated || <strong>UNDATED</strong>}
      </span>
      {s.url && (
        <a className={styles.disSourceUrl} href={s.url} target="_blank" rel="noreferrer noopener">
          {s.url}
        </a>
      )}
      {s.caveat && <span className={styles.disSourceCaveat}>{s.caveat}</span>}
    </li>
  )
}

/* ---- day detail --------------------------------------------------------- */

/**
 * The disruption block inside the day panel.
 *
 * Official dates and the effective window are shown as SEPARATE ROWS, never
 * merged, because they are different facts with different provenance and
 * merging them is how a 9-day holiday gets planned as the whole risk.
 */
export function DisruptionDayDetail({ dayKeyStr }) {
  const hits = useMemo(() => disruptionsOn(dayKeyStr), [dayKeyStr])
  const [openId, setOpenId] = useState(null)
  if (!hits.length) return null

  return (
    <section className={styles.disDay}>
      <h4 className={styles.disDayTitle}>
        Supply-chain disruption on this date
        <span className={styles.disDayCount}>{hits.length} active</span>
      </h4>
      <ul className={styles.disDayList}>
        {hits.map((h) => {
          const win = windowLabel(h)
          const wd = windowDays(h)
          const od = officialDays(h)
          const isOpen = openId === h.id
          return (
            <li key={h.id} className={styles.disDayItem} data-lane={h.lane} data-sev={h.severity}>
              <div className={styles.disItemHead}>
                <span className={styles.disItemLane} data-lane={h.lane}>
                  <i className={styles.disGlyph}>{h.laneMeta.glyph}</i>{h.laneMeta.code}
                </span>
                <span className={styles.disItemName}>
                  {h.name}
                  {h.localName && <span className={styles.disItemLocal}>{h.localName}</span>}
                </span>
                <span className={styles.disItemSev} data-sev={h.severity}>
                  {h.severityMeta.short}
                </span>
              </div>

              <div className={styles.disItemWhat}>{h.laneMeta.what} {h.severityMeta.blurb}</div>

              {/* OFFICIAL — what the government declares. */}
              <div className={styles.disRow}>
                <span className={styles.disRowLabel}>Official</span>
                <span className={styles.disRowValue}>
                  {officialLabel(h)}
                  {od != null && <em className={styles.disRowDays}>{od} {od === 1 ? 'day' : 'days'}</em>}
                  {h.lunar && <em className={styles.disRowLunar}>lunar — moves every year</em>}
                </span>
              </div>

              {/* EFFECTIVE WINDOW — when work is actually stopped. THE number
                  SR needs, and the one that is allowed to say UNKNOWN. */}
              <div className={`${styles.disRow} ${win.known ? '' : styles.disRowUnknown}`}>
                <span className={styles.disRowLabel}>Effective window</span>
                <span className={styles.disRowValue}>
                  {win.known ? (
                    <>
                      {win.text}
                      {wd != null && <em className={styles.disRowDays}>{wd} {wd === 1 ? 'day' : 'days'}</em>}
                      {win.forecast && <em className={styles.disRowWarn}>FORECAST — not an official arrangement. Do not plan a ship date against it.</em>}
                    </>
                  ) : (
                    <>
                      <strong className={styles.disUnknown}>UNKNOWN</strong>
                      <em className={styles.disRowWarn}>{win.reason}</em>
                      <em className={styles.disRowNote}>
                        Not estimated on purpose. An invented window is worse than none — somebody plans a ship date against it.
                      </em>
                    </>
                  )}
                </span>
              </div>

              {/* The reason this day matched at all. */}
              {h.reach === 'window' && (
                <div className={styles.disShoulderNote}>
                  ⚠️ This date is NOT an official holiday — it falls in the effective
                  disruption window around one. A statutory holiday calendar would
                  show this day as normal.
                </div>
              )}

              <button
                type="button"
                className={styles.disMoreBtn}
                aria-expanded={isOpen}
                onClick={() => setOpenId(isOpen ? null : h.id)}
              >
                {isOpen ? 'Hide sources & notes ▴' : 'Sources & notes ▾'}
              </button>

              {isOpen && (
                <div className={styles.disDetail}>
                  {h.official?.note && <p className={styles.disNote}>{h.official.note}</p>}
                  {h.official?.contradiction && <p className={styles.disNoteWarn}>{h.official.contradiction}</p>}
                  {h.window?.note && <p className={styles.disNote}>{h.window.note}</p>}
                  {h.window?.spread && <p className={styles.disNoteWarn}>{h.window.spread}</p>}
                  {h.window?.caveat && <p className={styles.disNoteWarn}>{h.window.caveat}</p>}
                  {h.window?.manufacturing && <p className={styles.disNote}>{h.window.manufacturing}</p>}
                  {h.window?.shoulderReason && <p className={styles.disNoteWarn}>{h.window.shoulderReason}</p>}
                  {h.note && <p className={styles.disNoteWarn}>{h.note}</p>}
                  {h.action && <p className={styles.disNoteAction}>{h.action}</p>}
                  {h.confidenceNote && <p className={styles.disNote}>Confidence: {h.confidenceNote}</p>}
                  {!h.confidenceNote && <p className={styles.disNote}>Confidence: {h.confidence}</p>}
                  {h.ethiopianYear && <p className={styles.disNote}>Ethiopian calendar: {h.ethiopianYear}</p>}
                  <ul className={styles.disSources}>
                    {h.official?.source && <SourceLine keyName={h.official.source} />}
                    {(h.window?.sources || []).map((k) => <SourceLine key={k} keyName={k} />)}
                  </ul>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/* ---- the Command Center banner ------------------------------------------ */

/**
 * ⭐ THE THING A REP IS SUPPOSED TO ENCOUNTER WITHOUT BEING TOLD TO LOOK.
 *
 * Sits above the month grid, so it is on screen the moment the calendar is —
 * not behind a toggle. States the month's risk, and names the collisions,
 * because the collision is the operational signal; the holiday list on its own
 * is reference data.
 */
export default function DisruptionOverlay({ monthLabel, monthEntries, items }) {
  const [showGaps, setShowGaps] = useState(false)

  const disruptedDays = useMemo(
    () => Object.values(monthEntries || {}).filter((e) => e.list.length),
    [monthEntries],
  )
  const makeupDays = useMemo(
    () => Object.values(monthEntries || {}).filter((e) => e.makeup),
    [monthEntries],
  )

  /* Collisions are scoped to the records the calendar is ALREADY showing, so
     the number agrees with what is on screen rather than with the whole book. */
  const collisions = useMemo(() => eventDateCollisions(items), [items])
  const monthCollisions = useMemo(() => {
    const keys = new Set(disruptedDays.map((e) => e.key))
    return collisions.filter((c) => keys.has(c.day))
  }, [collisions, disruptedDays])

  const lanesHit = useMemo(() => {
    const s = new Set()
    for (const e of disruptedDays) for (const h of e.list) s.add(h.lane)
    return ['JD', 'SRE', 'US'].filter((l) => s.has(l))
  }, [disruptedDays])

  const unknownWindows = useMemo(() => {
    const seen = new Map()
    for (const e of disruptedDays) {
      for (const h of e.list) if (!h.windowKnown) seen.set(h.id, h)
    }
    return [...seen.values()]
  }, [disruptedDays])

  const gap = KNOWN_GAPS.find((g) => g.id === 'delivery-window-link')

  return (
    <section className={styles.disBanner}>
      <div className={styles.disBannerHead}>
        <h3 className={styles.disBannerTitle}>Supply-chain disruption — {monthLabel}</h3>
        {/* The legend is HERE rather than hidden, because three lanes of marks
            on a grid are meaningless without it. */}
        <ul className={styles.disLegend}>
          {Object.values(LANES).map((l) => (
            <li key={l.code} className={styles.disLegendItem} data-lane={l.code}>
              <span className={styles.disMark} data-lane={l.code}>
                <i className={styles.disGlyph}>{l.glyph}</i>{l.code}
              </span>
              <span className={styles.disLegendText}>{l.label}</span>
            </li>
          ))}
          <li className={styles.disLegendItem}>
            <span className={styles.disMarkShoulder}>hollow</span>
            <span className={styles.disLegendText}>effective window, not an official holiday</span>
          </li>
        </ul>
      </div>

      {disruptedDays.length === 0 && makeupDays.length === 0 ? (
        <p className={styles.disBannerCalm}>
          No JD, SRE or US disruption recorded in {monthLabel} for 2026–2027.
          {' '}⚠️ That is the absence of a <em>recorded</em> disruption, not a guarantee of
          an open factory — JD&apos;s and SRE&apos;s own written schedules are not held by SR.
        </p>
      ) : (
        <>
          <p className={styles.disBannerLead}>
            <strong>{disruptedDays.length}</strong> {disruptedDays.length === 1 ? 'day' : 'days'} disrupted
            {lanesHit.length > 0 && <> across {lanesHit.join(' · ')}</>}
            {makeupDays.length > 0 && (
              <> · <strong>{makeupDays.length}</strong> mandated JD <em>working</em> weekend {makeupDays.length === 1 ? 'day' : 'days'}</>
            )}
          </p>

          {/* ⭐ THE COLLISION — the operational signal. */}
          {monthCollisions.length > 0 ? (
            <div className={styles.disCollide}>
              <h4 className={styles.disCollideTitle}>
                ⚠️ {monthCollisions.length} {monthCollisions.length === 1 ? 'championship lands' : 'championships land'} on a disrupted date
              </h4>
              <ul className={styles.disCollideList}>
                {monthCollisions.slice(0, 6).map((c) => (
                  <li key={c.record.id} className={styles.disCollideRow}>
                    <span className={styles.disCollideLane} data-lane={c.worst.lane}>
                      <i className={styles.disGlyph}>{c.worst.laneMeta.glyph}</i>{c.worst.laneMeta.code}
                    </span>
                    <span className={styles.disCollideName}>{c.record.name}</span>
                    <span className={styles.disCollideWhy}>
                      {c.day} · {c.worst.name} · {c.worst.severityMeta.short}
                    </span>
                  </li>
                ))}
              </ul>
              {monthCollisions.length > 6 && (
                <p className={styles.disCollideMore}>
                  and {monthCollisions.length - 6} more — open a marked date below for the full list.
                </p>
              )}
              {/* 🔴 THE HONEST BOUNDARY ON THE CLAIM. */}
              <p className={styles.disCollideCaveat}>
                🔴 This is an <strong>event-date</strong> collision, not a delivery-date collision.
                SR cannot compute a delivery window on this surface yet: championships are keyed on
                the event date, while production lives on the ClickUp journey order. No lead time has
                been assumed to bridge that — doing so would invent the answer.
                <button type="button" className={styles.disMoreBtn} onClick={() => setShowGaps(!showGaps)} aria-expanded={showGaps}>
                  {showGaps ? 'Hide what would connect it ▴' : 'What would connect it ▾'}
                </button>
              </p>
              {showGaps && gap && (
                <div className={styles.disDetail}>
                  <p className={styles.disNote}><strong>Why:</strong> {gap.why}</p>
                  <p className={styles.disNoteAction}><strong>To close:</strong> {gap.toClose}</p>
                </div>
              )}
            </div>
          ) : (
            disruptedDays.length > 0 && (
              <p className={styles.disBannerCalm}>
                No championship in the current view lands on a disrupted date this month.
                {' '}⚠️ Event dates only — a delivery window crossing a shutdown cannot be
                detected yet (see the collision note on any month that has one).
              </p>
            )
          )}

          {/* UNKNOWN windows are surfaced, not buried. A month full of holidays
              whose windows are unknown is NOT a safe month. */}
          {unknownWindows.length > 0 && (
            <p className={styles.disBannerUnknown}>
              ⚠️ {unknownWindows.length} of this month&apos;s {disruptedDays.length > 0 ? 'disruptions' : 'entries'} have an
              <strong> UNKNOWN effective window</strong>: {unknownWindows.map((h) => h.name).join(', ')}.
              {' '}Shown against official dates only. Open the date for the reason each one is unknown.
            </p>
          )}
        </>
      )}
    </section>
  )
}
